import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Shield, Shuffle } from 'lucide-react';
import { organizationAPI } from '../../services/api';
import { useToast } from '../shared/Toast';
import { DetailPanel, GHOST_BTN, INPUT_CLS, ICON_BTN, StatusChip } from '../shared/listPage';
import { OrgStore, serverMessage } from './orgTypes';
import { storeLabel } from './orgFormat';

/**
 * «Κωδικοί καταστήματος & χρηστών» σε συρτάρι (ως 14/10/2026 παράθυρο στη μέση). Ίδιες κλήσεις με πριν:
 * GET …/credentials, POST …/reset-password { password }, POST …/employees/:id/reset-pin { pin } — χωρίς
 * τιμή ο server βγάζει τυχαίο. Ο νέος κωδικός/PIN φαίνεται ΜΙΑ φορά, όσο είναι ανοιχτό το συρτάρι.
 */

interface CredEmployee {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  role?: string | null;
  role_label?: string | null;
  is_active?: boolean;
  has_pin?: boolean;
  pin_length?: number | null;
}

interface CredData {
  store: { username?: string | null; has_password?: boolean };
  employees: CredEmployee[];
}

const copyText = (txt: string) => { try { navigator.clipboard?.writeText(txt); } catch { /* ignore */ } };

const OrgCredentialsPanel: React.FC<{ orgId: number; store: OrgStore | null; onClose: () => void }> = ({ orgId, store, onClose }) => {
  const { t } = useTranslation(['orgDashboard', 'common', 'listPage']);
  const { showToast } = useToast();
  const [data, setData] = useState<CredData | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [reveal, setReveal] = useState<Record<string, string>>({}); // 'store' | 'emp_<id>' → νέο μυστικό
  const [input, setInput] = useState<Record<string, string>>({});
  const storeId = store?.id;

  useEffect(() => {
    if (!storeId) return undefined;
    let alive = true;
    setData(null); setReveal({}); setInput({}); setLoading(true);
    organizationAPI.storeCredentials(orgId, storeId)
      .then(d => { if (alive) setData(d); })
      .catch(e => {
        if (!alive) return;
        showToast('error', serverMessage(e, t('orgDashboard:credentials.loadFailed')));
        onClose();
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μία φόρτωση ανά κατάστημα
  }, [orgId, storeId]);

  const resetStorePwd = async (custom?: string) => {
    if (!storeId) return;
    setBusy('store');
    try {
      const d = await organizationAPI.resetStorePassword(orgId, storeId, custom || undefined);
      setReveal(r => ({ ...r, store: d.password }));
      setInput(i => ({ ...i, store: '' }));
      setData(c => (c ? { ...c, store: { ...c.store, has_password: true } } : c));
    } catch (e) {
      showToast('error', serverMessage(e, t('orgDashboard:errors.generic')));
    } finally { setBusy(null); }
  };

  const resetEmpPin = async (empId: number, custom?: string) => {
    if (!storeId) return;
    const key = `emp_${empId}`;
    setBusy(key);
    try {
      const d = await organizationAPI.resetEmployeePin(orgId, storeId, empId, custom || undefined);
      setReveal(r => ({ ...r, [key]: d.pin }));
      setInput(i => ({ ...i, [key]: '' }));
      setData(c => (c ? { ...c, employees: (c.employees || []).map(e => (e.id === empId ? { ...e, has_pin: true } : e)) } : c));
    } catch (e) {
      showToast('error', serverMessage(e, t('orgDashboard:errors.generic')));
    } finally { setBusy(null); }
  };

  // Συνάρτηση, όχι component μέσα στο render (θα ξαναδημιουργούνταν σε κάθε ζωγραφιά).
  const revealed = (label: string, value: string, testId: string) => (
    <div className="org-reveal" data-testid={testId}>
      <div className="min-w-0">
        <p className="org-reveal-label">{label}</p>
        <code className="org-reveal-value">{value}</code>
      </div>
      <button type="button" className={ICON_BTN} onClick={() => copyText(value)} aria-label={t('common:copy')} title={t('common:copy')}>
        <Copy className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );

  const SMALL_BTN = `${GHOST_BTN} !min-h-[40px] !px-3`;

  return (
    <DetailPanel
      open={!!store}
      onClose={onClose}
      title={store ? t('orgDashboard:credentials.title', { name: storeLabel(store) }) : ''}
      subtitle={t('orgDashboard:credentials.subtitle')}
      width="md"
      testId="org-credentials"
      footer={<button type="button" className={GHOST_BTN} onClick={onClose}>{t('listPage:close')}</button>}
    >
      {loading || !data ? (
        <p className="text-sm text-t3" role="status">{t('common:loading')}</p>
      ) : (
        <div className="space-y-7">
          <section aria-labelledby="org-cred-store">
            <h3 id="org-cred-store" className="org-panel-h">{t('orgDashboard:credentials.storeLogin')}</h3>
            <div className="org-cred-row">
              <span className="text-sm text-t3">{t('orgDashboard:credentials.username')}</span>
              <span className="flex items-center gap-1 min-w-0">
                <code className="text-sm text-t1 truncate">{data.store.username || '—'}</code>
                {data.store.username && (
                  <button type="button" className={ICON_BTN} onClick={() => copyText(data.store.username as string)} aria-label={t('common:copy')} title={t('common:copy')}>
                    <Copy className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </span>
            </div>
            {reveal.store
              ? revealed(t('orgDashboard:credentials.newPassword'), reveal.store, 'org-cred-store-reveal')
              : <p className="mt-2 text-xs text-t3">{t('orgDashboard:credentials.encrypted')}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                value={input.store || ''}
                onChange={ev => setInput(i => ({ ...i, store: ev.target.value }))}
                placeholder={t('orgDashboard:credentials.passwordPlaceholder')}
                aria-label={t('orgDashboard:credentials.passwordPlaceholder')}
                className={`${INPUT_CLS} !w-auto flex-1 min-w-[160px]`}
                data-testid="org-cred-store-input"
              />
              <button type="button" className={SMALL_BTN} disabled={busy === 'store' || (input.store || '').length < 6}
                onClick={() => resetStorePwd(input.store)} data-testid="org-cred-store-set">
                {t('orgDashboard:credentials.set')}
              </button>
              <button type="button" className={SMALL_BTN} disabled={busy === 'store'} onClick={() => resetStorePwd()}
                title={t('orgDashboard:credentials.randomPasswordTitle')} data-testid="org-cred-store-random">
                <Shuffle className="w-4 h-4" aria-hidden="true" /> {t('orgDashboard:credentials.random')}
              </button>
            </div>
          </section>

          <section aria-labelledby="org-cred-users">
            <h3 id="org-cred-users" className="org-panel-h">{t('orgDashboard:credentials.users', { count: data.employees.length })}</h3>
            {data.employees.length === 0 ? (
              <p className="text-sm text-t3">{t('orgDashboard:credentials.noUsers')}</p>
            ) : (
              <ul className="org-cred-list">
                {data.employees.map(e => {
                  const key = `emp_${e.id}`;
                  const name = [e.first_name, e.last_name].filter(Boolean).join(' ') || e.username || `#${e.id}`;
                  const len = e.pin_length || 4;
                  return (
                    <li key={e.id} className={e.is_active === false ? 'opacity-60' : undefined} data-testid={`org-cred-emp-${e.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-t1 truncate">
                            {name}
                            {e.is_active === false && <> <StatusChip tone="muted">{t('orgDashboard:credentials.inactive')}</StatusChip></>}
                          </p>
                          <p className="text-xs text-t3">{e.role_label || e.role || '—'} · {t('orgDashboard:credentials.pinDigits', { count: len })}</p>
                        </div>
                      </div>
                      {reveal[key] && revealed(t('orgDashboard:credentials.newPin'), reveal[key], `org-cred-emp-${e.id}-reveal`)}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          value={input[key] || ''}
                          onChange={ev => setInput(i => ({ ...i, [key]: ev.target.value.replace(/\D/g, '') }))}
                          inputMode="numeric"
                          placeholder={t('orgDashboard:credentials.pinPlaceholder', { count: len })}
                          aria-label={t('orgDashboard:credentials.pinFor', { name })}
                          className={`${INPUT_CLS} !w-auto flex-1 min-w-[140px]`}
                        />
                        <button type="button" className={SMALL_BTN} disabled={busy === key || (input[key] || '').length < 3}
                          onClick={() => resetEmpPin(e.id, input[key])}>
                          {t('orgDashboard:credentials.set')}
                        </button>
                        <button type="button" className={SMALL_BTN} disabled={busy === key} onClick={() => resetEmpPin(e.id)}
                          title={t('orgDashboard:credentials.randomPinTitle')}>
                          <Shuffle className="w-4 h-4" aria-hidden="true" /> {t('orgDashboard:credentials.random')}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <p className="flex items-start gap-2 text-xs text-t3">
            <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{t('orgDashboard:credentials.note')}</span>
          </p>
        </div>
      )}
    </DetailPanel>
  );
};

export default OrgCredentialsPanel;
